import { useCallback, useMemo } from 'react';

import type { DROPDOWN_ACTION_TYPE, DropdownAction, DropdownEntry } from './types';

export function useDropdownEntries(
    availableActions: DropdownAction[],
    showNotification: (message: string) => void,
): {
    dropdownEntries: DropdownEntry[];
    performEntryAction: (action: DROPDOWN_ACTION_TYPE) => void;
} {
    const actionRegister = useMemo(
        () =>
            availableActions.reduce(
                (register, action) => {
                    if (!action.action) {
                        return register;
                    }
                    return {
                        ...register,
                        [action.type]: action.action,
                    };
                },
                {} as Record<
                    DROPDOWN_ACTION_TYPE,
                    (showSnackbar: (message: string) => void) => void
                >,
            ),
        [availableActions],
    );

    const dropdownEntries = useMemo(
        () =>
            availableActions.map((action) => ({
                value: action.type,
                label: action.label,
                'data-cy': action['data-cy'],
            })),
        [availableActions],
    );

    const performEntryAction = useCallback(
        (action: DROPDOWN_ACTION_TYPE) => actionRegister[action]?.(showNotification),
        [actionRegister, showNotification],
    );

    return { dropdownEntries, performEntryAction };
}
